import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type TenantCapital = {
  propertyid: number
  tenantcapitalid: number
  type: string
}
// private module logic //

const id: string = "tenantcapital"
const initialState: ApiResult<TenantCapital[]> = { id, state: "init", value: [] }
const tenantcapitalStream = new BehaviorSubject<ApiResult<TenantCapital[]>>(initialState)
const tenantcapitalEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let tenantcapitalMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortTenantcapital(data: TenantCapital[]): TenantCapital[] {
  data.sort((a, b) => {
    let A = a["code"], B = b["code"]

    if(A > B) return 1
    else if(A < B) return -1
    else return 0
  });

  return data
}

function clear(): void {
  tenantcapitalStream.next({ id, state: 'ok', value: [] })
  tenantcapitalMap = {}
}

function get(tenantcapitalId: number): TenantCapital {
  return tenantcapitalMap[tenantcapitalId]
}

function refresh(): Promise<any> {
  const currentValue: TenantCapital[] = tenantcapitalStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  const type = {
    'leasing-commissions': 'lc',
    'other-capital': 'oc',
    'tenant-improvement': 'ti',
  }[window.location.pathname.split('/').pop() || '']
  if (_.isNil(propertyid) || _.isNil(type)) return Promise.resolve()
  return fetchTenantcapital(propertyid, type)
}

function fetchTenantcapital(
  propertyId: number,
  type: string = "ti",
  limit: number = 0,
  offset: number = 0
): Promise<TenantCapital[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  tenantcapitalStream.next({ id, state: 'busy', value: tenantcapitalStream.getValue().value })

  let url = `tenantcapital?propertyid=eq.${propertyId}&type=eq.${type}&order=code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `tenantcapital?propertyid=eq.${propertyId}&type=eq.${type}&limit=${limit}&offset=${offset}&order=code.asc.nullsfirst`
  }
  if (type === "all") {
    url = `tenantcapital?propertyid=eq.${propertyId}&order=code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: TenantCapital[]) => {
    _.forEach(records, (tenantcapital) => (tenantcapitalMap[tenantcapital.tenantcapitalid] = tenantcapital))
    // merge results unique by tenantcapitalid
    tenantcapitalStream.next({ id, state: 'ok', value: sortTenantcapital(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number, defaults: {}, type: string = "ti"): Promise<TenantCapital | { message: string }> {
  const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value
  tenantcapitalStream.next({ id, state: 'busy', value: curtenantcapitalValue })

  const payload = {
    propertyid,
    amtnew: null,
    amtrenew: null,
    amttype: defaults[`${type.toLowerCase()}AmtType`],
    inputtype: "basic",
    name: "",
    inflationdetail: _.includes(defaults[`${type.toLowerCase()}AmtType`], "%") ? null : {
      inflationMode: "namedItem",
      inflationNamedItemId: _.get(defaults, "tiInflation.inflationNamedItemId")
    },
    payoutdetail: {
      payoutMode: "namedItem",
      payoutNamedItemId: _.get(defaults, "tiPayout.payoutNamedItemId")
    },
    tenantcapitaldetail: null,
    type
  }

  return Client.post(`tenantcapital`, payload)
    .then((tenantcapital: TenantCapital) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, tenantcapital.tenantcapitalid),
        redofunc: () => create(propertyid, defaults)
      })
      tenantcapitalMap[tenantcapital.tenantcapitalid] = tenantcapital
      // merge results unique by tenantcapitalid
      const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value
      tenantcapitalStream.next({
        id,
        state: 'ok',
        value: _.unionBy([tenantcapital], curtenantcapitalValue, 'tenantcapitalid')
      })
      tenantcapitalEventStream.next({ type: 'TENANTCAPITAL_CREATED', payload: tenantcapital })
      return tenantcapital
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantcapitalStream.next({ id, state: 'error', error: err, value: curtenantcapitalValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchTenantcapital(propertyid, type)
      return err
    })
}

function copy(propertyid:number, tenantcapitalid:number, code:string, type:string="ti") {
  const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value;
  tenantcapitalStream.next({ id, state: 'busy', value: curtenantcapitalValue });

  const payload = {table: 'tenantcapital', recordid: tenantcapitalid, _synchronous: true, code: incrementUnique(code, "code", curtenantcapitalValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantcapital: TenantCapital) => {
      const newRecord = tenantcapital[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.tenantcapitalid),
        redofunc: () => copy(propertyid, tenantcapitalid, code, type)
      });
      tenantcapitalMap[newRecord.tenantcapitalid] = newRecord
      // merge results unique by tenantcapitalid
      const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value
      tenantcapitalStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], curtenantcapitalValue, 'tenantcapitalid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantcapitalEventStream.next({ type: 'TENANTCAPITAL_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantcapitalStream.next({ id, state: 'error', error: err, value: curtenantcapitalValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantcapital(propertyid, type)
      return err
    })
}

function copies(propertyid: number, tenantcapitalid: number, count: number, type:string="ti") {
  const curtenantcapitalValue = tenantcapitalStream.getValue().value;
  tenantcapitalStream.next({ id, state: 'busy', value: curtenantcapitalValue });

  const payload = {table: 'tenantcapital', recordid: tenantcapitalid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantcapitals: TenantCapital[]) => {
      push_undo({
        undofunc: () => _.forEach(tenantcapitals, newRecord => deleteOne(propertyid, newRecord.tenantcapitalid)),
        redofunc: () => copies(propertyid, tenantcapitalid, count, type)
      });
      _.forEach(tenantcapitals, newRecord => tenantcapitalMap[newRecord.tenantcapitalid] = newRecord)
      // merge results unique by tenantcapitalid
      const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value
      tenantcapitalStream.next({
        id,
        state: 'ok',
        value: _.unionBy(tenantcapitals, curtenantcapitalValue, 'tenantcapitalid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantcapitalEventStream.next({ type: 'TENANTCAPITAL_COPIED', payload: tenantcapitals })
      return tenantcapitals
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantcapitalStream.next({ id, state: 'error', error: err, value: curtenantcapitalValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantcapital(propertyid, type)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  tenantcapitalid: number,
  type: string="ti"
): Promise<boolean | { message: string }> {
  const curtenantcapitalValue: TenantCapital[] = tenantcapitalStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, tenantcapitalid)
  const refreshFn = () => fetchTenantcapital(propertyid, type)
  if (!(await handleReferences('tenantcapital', tenantcapitalid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted tenantcapitalid
  const value = _(curtenantcapitalValue)
    .filter((p) => p.tenantcapitalid !== tenantcapitalid)
    .value()
  value.forEach((p) => (tenantcapitalMap[p.tenantcapitalid] = p))

  tenantcapitalStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(tenantcapitalid).then(() =>
  Client.del(`tenantcapital?tenantcapitalid=eq.${tenantcapitalid}`)
    .then((tenantcapital: TenantCapital) => {
      delete tenantcapitalMap[tenantcapital.tenantcapitalid]
      push_undo({
        undofunc: () => {
          Client.post('tenantcapital', tenantcapital)
          tenantcapitalMap[tenantcapital.tenantcapitalid] = tenantcapital
          tenantcapitalStream.next({ id, state: 'ok', value: curtenantcapitalValue })
        },
        redofunc: () => deleteOne(propertyid, tenantcapitalid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantcapitalStream.next({ id, state: 'error', error: err, value: curtenantcapitalValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchTenantcapital(propertyid, type)
      return err
    })
  )
}

function patch(tenantcapital: TenantCapital): Promise<TenantCapital | { message: string }> {
  const currentValue: TenantCapital[] = tenantcapitalStream.getValue().value
  const tenantcapitalid: number = tenantcapital.tenantcapitalid
  const currentTenantcapital = _.find(currentValue, { tenantcapitalid })
  const changeset = diff_obj(tenantcapital, currentTenantcapital)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentTenantcapital),
    redofunc: () => patch(tenantcapital)
  })

  // optimistic update
  tenantcapitalStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {tenantcapitalid}), tenantcapital)
  })

  return _patches_.watch(tenantcapitalid, Client.patch(`tenantcapital?tenantcapitalid=eq.${tenantcapitalid}`, changeset)
    .then((mf: TenantCapital[]) => {
      _.forEach(mf, (tenantcapital) => (tenantcapitalMap[tenantcapital.tenantcapitalid] = tenantcapital))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantcapitalStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentTenantcapital, 'propertyid')
      if (propertyid) fetchTenantcapital(propertyid, tenantcapital.type)
      return err
    }))

}

// public module interface //

const tenantcapitalService = {
  stream: () => tenantcapitalStream.asObservable(),
  value: () => tenantcapitalStream.getValue().value,
  eventStream: () => tenantcapitalEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchTenantcapital,
  patch,
  refresh
}

export default tenantcapitalService
